import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/UI/Button';
import { Box } from '@/layout';
import { Modal } from '@mui/material';

import { ModalActions, ModalForm, ModalTitle } from '../style';
import { successHandler, errorHandler } from '@/utils/toastHandler';
import { Close } from '@/components/Icon';
import { InputWrapper } from '@/components/Container/AuthContainer/styles';
import { TextField } from '@mui/material';
import { moveFileToFolder, getFolderTree } from '@/api/document.api';
import { FormSelect } from '@/components/UI/Select';

interface ModalProps {
  fileId: string;
  currentFolderId: number | null;
  show: boolean;
  setShowMoveToFolderModal: (show: boolean) => void;
  getDocuments: (id: number | null) => void;
}

const MoveToModal = ({ fileId, currentFolderId, show, setShowMoveToFolderModal, getDocuments }: ModalProps) => {
  // @ts-ignore
  const { t } = useTranslation();

  const [folderOptions, setFolderOptions] = useState<{ value: string; text: string }[]>([]);

  const {
    control,
    resetField,
    setValue,
    handleSubmit
  } = useForm<{ targetFolderId: string }>();

  const onSubmit = handleSubmit(async (data) => {
    try {
      let folderId: string | null = data.targetFolderId;
      if (data.targetFolderId === 'null') {
        folderId = null;
      }
      const res = (await moveFileToFolder({ documentId: fileId, folderId: folderId ? Number(folderId) : null })).data;
      if (res.success) {
        getDocuments(currentFolderId);
        // successHandler(t('success.addFolder'));
        successHandler('Move Folder Success');
        resetField('targetFolderId');
        setShowMoveToFolderModal(false);
      }
    } catch (error: any) {
      console.log(error);
      errorHandler('Move Folder Failed: ' + error.response.data.data);
    } finally {

    }
  });

  const initData = async () => {
    try {
      const res = (await getFolderTree()).data;
      if (res.success) {
        // 透過遞迴將所有res.data和res.data[n].children中所有資料攤平並紀錄id和name
        const flatten = (data: FolderTreeNode[]): { value: string; text: string }[] => {
          let result: { value: string; text: string }[] = [];
          data.forEach(item => {
            result.push({ value: item.id.toString(), text: item.name });
            if (item.children && item.children.length > 0) {
              result = result.concat(flatten(item.children));
            }
          });
          return result;
        };

        const flatData = flatten(res.data);

        // 完成後需檢查當前選擇到的fileId是否出現在於攤平後的資料中(因資料夾也可移動), 若出現則將選項從陣列中移除
        const index = flatData.findIndex(item => item.value === String(currentFolderId));
        if (index !== -1) {
          flatData.splice(index, 1);
        }

        // 根目錄也算一個選項, 需手動加入
        flatData.unshift({ value: 'null', text: 'Root Folder' });
        setFolderOptions(flatData);
      }
    } catch (error) {
      console.log('Error fetching folder tree:', error);
    }
  }

  useEffect(() => {
    if (!show) return;
    initData();
    resetField('targetFolderId');
  }, [fileId, show]);

  return (
    <Modal
      open={show}
      onClose={() => setShowMoveToFolderModal(false)}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      disableEnforceFocus
    >
      <ModalForm onSubmit={onSubmit}>
        <Close.ButtonClose
          type="button"
          onClick={() => {
            setShowMoveToFolderModal(false);
          }}
        >
          <Close.CloseSVG />
        </Close.ButtonClose>
        <ModalTitle>Move to</ModalTitle>
        <InputWrapper style={{ height: 'auto' }}>
          <FormSelect
            control={control}
            data={folderOptions}
            inputLabel={'To'}
            selectLabel={'folder'}
            name={'targetFolderId'}
          />
        </InputWrapper>
        <ModalActions>
          <Button type="submit" size={'lg'} variant={'primary'} rounded={false}>
            {t('buttons.ok')}
          </Button>
          <Button
            type="button"
            size={'lg'}
            variant={'primary-invert'}
            color="primary"
            onClick={() => setShowMoveToFolderModal(false)}
            style={{ textDecoration: 'underline' }}
          >
            {t('buttons.cancel')}
          </Button>
        </ModalActions>
      </ModalForm>
    </Modal>
  );
};

export default MoveToModal;
